import { useState } from 'react';

// prop-type is a library for typechecking of props
import PropTypes from 'prop-types';

// formik components
import { ErrorMessage, Field } from 'formik';

// Soft UI Dashboard PRO React components
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import MuiTextField from '@mui/material/TextField';
import { TextField } from '../ComponentActionsGenerator/components/StyledComponents';
import ListItemIcon from '@mui/material/ListItemIcon';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import SoftSelect from 'components/SoftSelect';
import SoftTagInput from 'components/SoftTagInput';
import SoftEditor from 'components/SoftEditor';
import SoftDatePicker from 'components/SoftDatePicker';
import SoftButton from 'components/SoftButton';
import { ThemedContentCopyIcon, ThemedCheckCircleIcon } from 'assets/CXFIcons/Designer/ContextActions';
import Tooltip from '@mui/material/Tooltip';
import CredentialManangementAction from 'layouts/pages/credential-management/components/CredentialManagementAction';
import CredentialDialogForm from 'layouts/pages/credential-management/components/CredentialDialogForm';
import CredentialDeleteDialog from 'layouts/pages/credential-management/components/CredentialDeleteDialog';
import { THEME_COLORS, THEME_TYPES } from 'constants/theme';
import { components } from 'react-select';
import IconButton from '@mui/material/IconButton';
import { ThemedTrashIcon } from 'assets/CXFIcons/Triggers';

const CredentialsSelect = styled(SoftSelect)(() => ({ width: '100%' }));

const DisabledTextField = styled(MuiTextField)(({ theme }) => ({
	width: '100%',
	'& textarea': { width: '100% !important' },
	'& .MuiInputLabel-root.Mui-disabled': {
		color: theme.palette.text.primary,
		opacity: 1
	},
	'& .MuiInputBase-root': {
		'&.Mui-disabled': {
			backgroundColor: `${theme.palette.mode === THEME_TYPES.DARK ? 'var(--input-bg-color)' : THEME_COLORS[theme.palette.mode.toUpperCase()].DISABLED_BG} !important`,
			borderColor: `${theme.palette.mode === THEME_TYPES.DARK ? 'var(--input-border-color)' : 'rgba(0, 0, 0, 0.23)'} !important`,
			opacity: 1
		}
	},
	'& .MuiInputBase-input.Mui-disabled, & textarea.Mui-disabled': {
		color: theme.palette.mode === THEME_TYPES.DARK ? 'rgba(255, 255, 255, 0.45) !important' : `${theme.palette.text.secondary} !important`,
		WebkitTextFillColor: theme.palette.mode === THEME_TYPES.DARK ? 'rgba(255, 255, 255, 0.45) !important' : `${theme.palette.text.secondary} !important`,
		opacity: 1
	}
}));

const Option = (props) => {
	return (
		<components.Option {...props}>
			<div style={{ fontSize: 14, fontWeight: 'bold' }}>{props.data.label}</div>
			{props.data.subLabel && <div style={{ fontSize: 12 }}>{props.data.subLabel}</div>}
		</components.Option>
	);
};

const styles = { w100: { width: '100%' } };

function FormField({
	options = [],
	label = '',
	type,
	name = '',
	defaultValue = {},
	defaultValues = [],
	isMultiline,
	handleLinkClick = Function(),
	style,
	required = false,
	ownedSharedCredentials,
	inlineSwitch = false,
	onChange = Function(),
	index,
	secondaryCopySuffix = '',
	...rest
}) {
	const [copiedType, setCopiedType] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const theme = useTheme();

	const handleCopy = (text, type) => {
		if (!text) return;

		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopiedType(type);
				setTimeout(() => {
					setCopiedType(null);
				}, 2000);
			})
			.catch((error) => {
				console.error('Error copying text to clipboard:', error);
			});
	};

	const handleCopyBaseUrl = () => {
		handleCopy(rest.value, 'base');
	};

	const handleCopyExecutionLogsUrl = () => {
		const baseUrl = rest.value;

		if (!baseUrl) return;

		const appendedUrl = `${baseUrl}${secondaryCopySuffix}`;
		handleCopy(appendedUrl, 'extended');
	};

	const getInput = () => {
		switch (type) {
			case 'zipFileUpload':
			case 'file':
				return (
					<Field name={name}>
						{({ form }) => (
							<SoftInput
								{...rest}
								type={type}
								name={name}
								value={undefined}
								onChange={(event) => {
									const selectedFile = event.target.files?.[0] || '';
									form.setFieldValue(name, selectedFile);
									form.setFieldTouched(name, true, false);
									onChange(event);
								}}
								className={rest.error ? 'field-error' : ''}
							/>
						)}
					</Field>
				);
			case 'text':
			case 'password':
			case 'number':
				return isMultiline ? (
					<Field name={name}>
						{({ field }) => (
							<SoftInput
								{...field}
								{...rest}
								type={type}
								name={name}
								onChange={(event) => {
									field.onChange(event);
									onChange?.(event);
								}}
								multiline
								rows={rest.rows ? rest.rows : 5}
								className={rest.error ? 'field-error' : ''}
							/>
						)}
					</Field>
				) : (
					<Field name={name}>
						{({ field }) => (
							<SoftInput
								{...field}
								{...rest}
								type={type}
								name={name}
								onChange={(event) => {
									field.onChange(event);
									onChange?.(event);
								}}
								className={rest.error ? 'field-error' : ''}
							/>
						)}
					</Field>
				);
			case 'id':
			case 'token':
				return (
					<Field name={name}>
						{() => {
							return (
								<SoftBox width="100%" display="flex" flexDirection={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems="center" gap={1}>
									<SoftInput {...rest} type={type === 'token' ? 'password' : type} />
									<Tooltip title={copiedType === 'base' ? 'Copied' : 'Copy'}>
										<SoftButton color="primary" variant="text" onClick={handleCopyBaseUrl}>
											{copiedType === 'base' ? (
												<ThemedCheckCircleIcon color={theme.palette.mode === 'dark' ? 'rgb(170, 139, 253)' : '#7443FF'} />
											) : (
												<ThemedContentCopyIcon color={theme.palette.mode === 'dark' ? 'rgb(170, 139, 253)' : '#7443FF'} />
											)}
										</SoftButton>
									</Tooltip>
								</SoftBox>
							);
						}}
					</Field>
				);
			case 'readonlyEndpoint':
				return (
					<Field name={name}>
						{() => {
							return (
								<SoftBox width="100%" display="flex" flexDirection={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems="center" gap={1}>
									<DisabledTextField {...rest} slotProps={{ input: { disabled: true } }} />

									<SoftBox display="flex" flexDirection="column" alignItems="center" gap={1}>
										<Tooltip title={copiedType ? 'Copied' : 'Copy URL'} placement="left">
											<SoftButton color="primary" variant="text" onClick={(e) => setAnchorEl(e.currentTarget)}>
												{copiedType ? (
													<ThemedCheckCircleIcon color={theme.palette.mode === 'dark' ? 'rgb(170, 139, 253)' : '#7443FF'} />
												) : (
													<ThemedContentCopyIcon color={theme.palette.mode === 'dark' ? 'rgb(170, 139, 253)' : '#7443FF'} />
												)}
											</SoftButton>
										</Tooltip>
										<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
											<MenuItem
												onClick={() => {
													handleCopyBaseUrl();
													setAnchorEl(null);
												}}
											>
												Copy Base URL
											</MenuItem>
											<MenuItem
												onClick={() => {
													handleCopyExecutionLogsUrl();
													setAnchorEl(null);
												}}
											>
												Copy Extended URL with User ID
											</MenuItem>
										</Menu>
									</SoftBox>
								</SoftBox>
							);
						}}
					</Field>
				);
			case 'editor':
				return (
					<Field name={name}>
						{({ field, form }) => {
							return (
								<SoftEditor
									{...rest}
									value={field.value}
									onChange={(e) => {
										const links = document.querySelectorAll(".ql-editor a[rel='noopener noreferrer']");
										if (links.length) {
											links.forEach((l) => {
												let href = l.getAttribute('href');
												if (href.indexOf('http') === -1) {
													l.setAttribute('href', `http://${href}`);
												}
												l.removeEventListener('click', handleLinkClick);
												l.addEventListener('click', handleLinkClick);
											});
										}
										form.setFieldValue(field.name, e);
									}}
								/>
							);
						}}
					</Field>
				);
			case 'tag':
				return (
					<Field name={name}>
						{({ field, form }) => {
							return (
								<SoftTagInput
									{...rest}
									value={field.value}
									onChange={(value) => {
										form.setFieldValue(field.name, Array.isArray(value) ? value.map((item) => item.trim().replace(/\s+/g, ' ')) : []);
									}}
								/>
							);
						}}
					</Field>
				);
			case 'select':
				return (
					<Field name={name}>
						{({ field, form, meta }) => {
							const { touched } = form;
							const isError = Boolean(meta.error) && touched[field.name];

							return (
								<SoftSelect
									{...rest}
									defaultValue={defaultValue}
									options={options}
									error={isError}
									onChange={({ value }) => {
										form.setFieldValue(field.name, value);
										// debouncedValidate();
										onChange(value);
									}}
									onBlur={() => {
										form.setFieldTouched(field.name, true);
									}}
								/>
							);
						}}
					</Field>
				);
			case 'multiSelect':
				return (
					<Field name={name}>
						{({ field, form }) => {
							return (
								<SoftSelect
									{...rest}
									defaultValue={defaultValues}
									options={options}
									isMulti
									onChange={(value) => {
										form.setFieldValue(field.name, value);
									}}
								/>
							);
						}}
					</Field>
				);
			case 'selectWithDetails':
				return (
					<Field name={name}>
						{({ field, form, meta }) => {
							const { touched } = form;
							const isError = Boolean(meta.error) && touched[field.name];

							return (
								<SoftSelect
									{...rest}
									defaultValue={defaultValue}
									options={options}
									error={isError}
									onChange={(selected) => {
										let value;

										if (rest.isMulti) {
											value = selected ? selected.map((sel) => sel.value) : [];
										} else {
											value = selected ? selected.value : '';
										}
										form.setFieldValue(field.name, value);
									}}
									onBlur={() => {
										form.setFieldTouched(field.name, true);
									}}
									components={{ Option }}
								/>
							);
						}}
					</Field>
				);
			case 'date':
				return (
					<Field name={name}>
						{({ field, form, meta }) => {
							const { touched } = form;
							const isError = Boolean(meta.error) && touched[field.name];
							return (
								<SoftDatePicker
									{...rest}
									className="date-picker"
									input={{ placeholder: 'Select a date', error: isError }}
									value={field.value}
									onChange={(value, valueStr) => {
										form.setFieldValue(field.name, valueStr);
									}}
									onClose={() => {
										form.setFieldTouched(field.name, true);
									}}
								/>
							);
						}}
					</Field>
				);
			case 'boolean':
				return (
					<Field name={name}>
						{({ field, form }) => {
							return (
								<Switch
									disabled={rest.disabled}
									className={inlineSwitch ? 'inline-switch' : 'flow-props-switch'}
									checked={field.value}
									onChange={(e) => {
										form.setFieldValue(field.name, e.target.checked);
									}}
									color="primary"
								/>
							);
						}}
					</Field>
				);
			case 'checkboxGroup':
				return (
					<Field name={name}>
						{({ field, form }) => {
							return (
								<SoftBox className="checkbox-group-wrapper">
									{options.map((opt) => {
										return (
											<FormControlLabel
												key={opt.label}
												control={
													<Switch
														value={opt.value}
														checked={field.value?.includes(opt.value)}
														onChange={(e) => {
															form.setFieldValue(field.name, e.target.checked ? [...field.value, opt.value] : field.value?.filter((f) => f !== opt.value));
														}}
														color="primary"
														disabled={rest.disabled}
													/>
												}
												label={opt.label}
											/>
										);
									})}
								</SoftBox>
							);
						}}
					</Field>
				);
			case 'credentials':
				return (
					<Field name={name}>
						{({ field, form, meta }) => {
							const { touched } = form;
							const isError = Boolean(meta.error) && touched[field.name];
							let credentialData = ownedSharedCredentials.find((credential) => credential.id === field.value);
							/* const debouncedValidate = debounce(() => {
                form.validateField(field.name);
              }, 100); */

							return (
								<SoftBox width="100%" display="flex" flexDirection={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems="end" gap={1}>
									<CredentialsSelect
										{...rest}
										defaultValue={defaultValue}
										options={options}
										error={isError}
										onChange={({ value }) => {
											form.setFieldValue(field.name, value);
											onChange(value);
										}}
										onBlur={() => {
											form.setFieldTouched(field.name, true);
										}}
									/>
									<CredentialManangementAction credentialData={credentialData} iconOrientation="vertical" isEditor />
									<CredentialDialogForm />
									<CredentialDeleteDialog />
								</SoftBox>
							);
						}}
					</Field>
				);
			case 'keyValue':
				return (
					<Field name={name}>
						{({ field, form }) => {
							return (
								<SoftBox display="flex" alignItems="center" width="100%">
									<SoftBox width="100%">
										<SoftBox sx={{ marginBottom: '12px' }}>
											<TextField
												label={label || 'Key'}
												name="key"
												placeholder="Key"
												sx={styles.w100}
												value={field.value[index]?.key || ''}
												onChange={(e) => {
													form.setFieldValue(
														field.name,
														field.value.map((item, idx) => {
															if (idx === index) {
																return { ...item, key: e.target.value };
															}
															return item;
														})
													);
												}}
												type="text"
												variant="outlined"
												disabled={rest.disabled}
											/>
										</SoftBox>
										<SoftBox>
											<TextField
												label="Value"
												name="value"
												placeholder=""
												sx={styles.w100}
												value={field.value[index]?.value || ''}
												onChange={(e) =>
													// form.setFieldValue("value", e.target.value)
													form.setFieldValue(
														field.name,
														field.value.map((item, idx) => {
															if (idx === index) {
																return { ...item, value: e.target.value };
															}
															return item;
														})
													)
												}
												type="text"
												variant="outlined"
												disabled={rest.disabled}
											/>
										</SoftBox>
									</SoftBox>
									{rest.disabled ? null : (
										<ListItemIcon className="delete-variable-icon-wrapper" sx={{ top: '15px' }}>
											<Tooltip placement="left" title="Delete">
												<IconButton
													className="delete-variable-icon"
													onClick={(e) => {
														e.stopPropagation();
														// deleteKeyValue(action.field, idx);
														form.setFieldValue(
															field.name,
															field.value.filter((item, idx) => idx !== index)
														);
													}}
													fontSize="medium"
												>
													<ThemedTrashIcon width="26px" height="26px" />
												</IconButton>
											</Tooltip>
										</ListItemIcon>
									)}
								</SoftBox>
							);
						}}
					</Field>
				);
		}
	};

	return (
		<SoftBox style={{ ...style, display: rest.isHidden ? 'none' : 'block' }}>
			<SoftBox mb={1.5} display={inlineSwitch ? 'flex' : 'block'} sx={{ alignItems: inlineSwitch ? 'end' : 'center' }}>
				{label && type !== 'keyValue' && (
					<SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block" sx={{ order: inlineSwitch ? 1 : 0, marginLeft: inlineSwitch ? 1 : 0.5 }}>
						<SoftTypography component="label" variant="caption" fontWeight="bold" textTransform="capitalize">
							{label} {required && <span style={{ color: '#ff667c' }}>*</span>}
						</SoftTypography>
					</SoftBox>
				)}
				{getInput()}
				<SoftBox mt={0.75}>
					<SoftTypography component="div" variant="caption" color="error">
						<ErrorMessage name={name} />
					</SoftTypography>
				</SoftBox>
			</SoftBox>
		</SoftBox>
	);
}

// typechecking props for FormField
FormField.propTypes = {
	label: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(PropTypes.object),
	defaultValue: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), label: PropTypes.string })
};

export default FormField;
